import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './NotificationTemplates.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5030/api';

interface Template {
  template_id: string;
  template_name: string;
  notification_type: string;
  channel: 'email' | 'sms' | 'in_app';
  subject: string | null;
  body: string;
  variables: Record<string, string> | null;
  is_active: boolean;
  is_default: boolean;
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface Stats {
  total: number;
  by_channel: Array<{ channel: string; count: string }>;
  by_type: Array<{ notification_type: string; count: string }>;
}

const NotificationTemplates: React.FC = () => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    template_name: '',
    notification_type: '',
    channel: 'email' as 'email' | 'sms' | 'in_app',
    subject: '',
    body: '',
    variables: {} as Record<string, string>,
    is_active: true,
    is_default: false,
    description: '',
  });

  const [newVariableKey, setNewVariableKey] = useState('');
  const [newVariableValue, setNewVariableValue] = useState('');

  useEffect(() => {
    fetchTemplates();
    fetchStats();
  }, [selectedChannel, selectedType]);

  useEffect(() => {
    if (successMessage || errorMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, errorMessage]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/notification-templates?`;
      if (selectedChannel !== 'all') url += `channel=${selectedChannel}&`;
      if (selectedType !== 'all') url += `notification_type=${selectedType}&`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result.success) {
        setTemplates(result.data.templates);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setErrorMessage('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/notification-templates/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const result = await response.json();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleOpenModal = (template: Template | null = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({
        template_name: template.template_name,
        notification_type: template.notification_type,
        channel: template.channel,
        subject: template.subject || '',
        body: template.body,
        variables: template.variables || {},
        is_active: template.is_active,
        is_default: template.is_default,
        description: template.description || '',
      });
    } else {
      setEditingTemplate(null);
      setFormData({
        template_name: '',
        notification_type: '',
        channel: 'email',
        subject: '',
        body: '',
        variables: {},
        is_active: true,
        is_default: false,
        description: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setNewVariableKey('');
    setNewVariableValue('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const url = editingTemplate
        ? `${API_URL}/notification-templates/${editingTemplate.template_id}`
        : `${API_URL}/notification-templates`;

      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setSuccessMessage(
          editingTemplate
            ? 'Template updated successfully'
            : 'Template created successfully'
        );
        handleCloseModal();
        fetchTemplates();
        fetchStats();
      } else {
        setErrorMessage(result.message || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setErrorMessage('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/notification-templates/${templateId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const result = await response.json();

      if (result.success) {
        setSuccessMessage('Template deleted successfully');
        fetchTemplates();
        fetchStats();
      } else {
        setErrorMessage(result.message || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      setErrorMessage('Failed to delete template');
    }
  };

  const handleDuplicateTemplate = async (template: Template) => {
    const newName = prompt(
      'Enter a name for the duplicated template:',
      `Copy of ${template.template_name}`
    );

    if (!newName) return;

    try {
      const response = await fetch(
        `${API_URL}/notification-templates/${template.template_id}/duplicate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ new_template_name: newName }),
        }
      );

      const result = await response.json();

      if (result.success) {
        setSuccessMessage('Template duplicated successfully');
        fetchTemplates();
        fetchStats();
      } else {
        setErrorMessage(result.message || 'Failed to duplicate template');
      }
    } catch (error) {
      console.error('Error duplicating template:', error);
      setErrorMessage('Failed to duplicate template');
    }
  };

  const handlePreview = async () => {
    try {
      const response = await fetch(`${API_URL}/notification-templates/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          body: formData.body,
          variables: formData.variables,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setPreviewText(result.data.preview);
        setShowPreview(true);
      } else {
        setErrorMessage('Failed to generate preview');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      setErrorMessage('Failed to generate preview');
    }
  };

  const handleAddVariable = () => {
    if (!newVariableKey.trim()) return;

    setFormData({
      ...formData,
      variables: {
        ...formData.variables,
        [newVariableKey]: newVariableValue,
      },
    });

    setNewVariableKey('');
    setNewVariableValue('');
  };

  const handleRemoveVariable = (key: string) => {
    const newVariables = { ...formData.variables };
    delete newVariables[key];
    setFormData({ ...formData, variables: newVariables });
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return '📧';
      case 'sms':
        return '💬';
      case 'in_app':
        return '🔔';
      default:
        return '📄';
    }
  };

  return (
    <div className="notification-templates">
      <div className="page-header">
        <div className="header-content">
          <h1>Notification Templates</h1>
          <p className="header-subtitle">
            Manage customizable templates for emails, SMS, and in-app notifications
          </p>
        </div>
        <button className="btn-primary" onClick={() => handleOpenModal()}>
          + Create Template
        </button>
      </div>

      {successMessage && (
        <div className="success-message">
          <span>✓</span> {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="error-message">
          <span>✗</span> {errorMessage}
        </div>
      )}

      {/* Statistics */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">📊</div>
            <div className="stat-content">
              <h3>Total Templates</h3>
              <p className="stat-value">{stats.total}</p>
            </div>
          </div>

          {stats.by_channel.map((item) => (
            <div className="stat-card channel" key={item.channel}>
              <div className="stat-icon">{getChannelIcon(item.channel)}</div>
              <div className="stat-content">
                <h3>{item.channel.toUpperCase()}</h3>
                <p className="stat-value">{item.count}</p>
                <p className="stat-sub">templates</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Channel:</label>
          <select
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Channels</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="in_app">In-App</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="payment_reminder">Payment Reminder</option>
            <option value="payment_overdue">Payment Overdue</option>
            <option value="loan_approved">Loan Approved</option>
            <option value="loan_rejected">Loan Rejected</option>
            <option value="loan_disbursed">Loan Disbursed</option>
          </select>
        </div>
      </div>

      {/* Templates List */}
      <div className="templates-list">
        {loading ? (
          <div className="loading">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <h3>No templates found</h3>
            <p>Create your first notification template to get started</p>
          </div>
        ) : (
          <div className="templates-grid">
            {templates.map((template) => (
              <div className="template-card" key={template.template_id}>
                <div className="template-header">
                  <div className="template-title">
                    <span className="channel-icon">
                      {getChannelIcon(template.channel)}
                    </span>
                    <h3>{template.template_name}</h3>
                  </div>
                  <div className="template-badges">
                    {template.is_default && (
                      <span className="badge default">Default</span>
                    )}
                    {!template.is_active && (
                      <span className="badge inactive">Inactive</span>
                    )}
                  </div>
                </div>

                <div className="template-details">
                  <div className="detail-row">
                    <span className="label">Type:</span>
                    <span className="value">{template.notification_type}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Channel:</span>
                    <span className="value">{template.channel}</span>
                  </div>
                  {template.subject && (
                    <div className="detail-row">
                      <span className="label">Subject:</span>
                      <span className="value">{template.subject}</span>
                    </div>
                  )}
                  {template.description && (
                    <div className="detail-row">
                      <span className="label">Description:</span>
                      <span className="value">{template.description}</span>
                    </div>
                  )}
                </div>

                <div className="template-variables">
                  <strong>Variables:</strong>
                  {template.variables && Object.keys(template.variables).length > 0 ? (
                    <div className="variables-list">
                      {Object.keys(template.variables).map((key) => (
                        <span key={key} className="variable-tag">
                          {`{{${key}}}`}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="no-variables">No variables</span>
                  )}
                </div>

                <div className="template-actions">
                  <button
                    className="btn-secondary"
                    onClick={() => handleOpenModal(template)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => handleDuplicateTemplate(template)}
                  >
                    Duplicate
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => handleDeleteTemplate(template.template_id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTemplate ? 'Edit Template' : 'Create Template'}</h3>
              <button className="close-button" onClick={handleCloseModal}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>Template Name *</label>
                    <input
                      type="text"
                      value={formData.template_name}
                      onChange={(e) =>
                        setFormData({ ...formData, template_name: e.target.value })
                      }
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Notification Type *</label>
                    <select
                      value={formData.notification_type}
                      onChange={(e) =>
                        setFormData({ ...formData, notification_type: e.target.value })
                      }
                      required
                    >
                      <option value="">Select type</option>
                      <option value="payment_reminder">Payment Reminder</option>
                      <option value="payment_overdue">Payment Overdue</option>
                      <option value="loan_approved">Loan Approved</option>
                      <option value="loan_rejected">Loan Rejected</option>
                      <option value="loan_disbursed">Loan Disbursed</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Channel *</label>
                    <select
                      value={formData.channel}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          channel: e.target.value as 'email' | 'sms' | 'in_app',
                        })
                      }
                      required
                    >
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                      <option value="in_app">In-App</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Subject {formData.channel === 'email' && '*'}</label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      required={formData.channel === 'email'}
                      disabled={formData.channel === 'sms'}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Body *</label>
                  <textarea
                    value={formData.body}
                    onChange={(e) =>
                      setFormData({ ...formData, body: e.target.value })
                    }
                    rows={8}
                    required
                  />
                  <small>
                    Use {`{{variable_name}}`} for dynamic content
                  </small>
                </div>

                {/* Variables Management */}
                <div className="variables-section">
                  <h4>Template Variables</h4>
                  <div className="variables-manager">
                    <div className="add-variable">
                      <input
                        type="text"
                        placeholder="Variable name"
                        value={newVariableKey}
                        onChange={(e) => setNewVariableKey(e.target.value)}
                      />
                      <input
                        type="text"
                        placeholder="Description"
                        value={newVariableValue}
                        onChange={(e) => setNewVariableValue(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleAddVariable}
                      >
                        + Add
                      </button>
                    </div>

                    <div className="variables-list">
                      {Object.entries(formData.variables).map(([key, value]) => (
                        <div key={key} className="variable-item">
                          <span className="variable-key">{`{{${key}}}`}</span>
                          <span className="variable-value">{value}</span>
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => handleRemoveVariable(key)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData({ ...formData, is_active: e.target.checked })
                        }
                      />
                      <span>Active</span>
                    </label>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.is_default}
                        onChange={(e) =>
                          setFormData({ ...formData, is_default: e.target.checked })
                        }
                      />
                      <span>Set as Default</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handlePreview}
                >
                  Preview
                </button>
                <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingTemplate ? 'Update' : 'Create'} Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Template Preview</h3>
              <button className="close-button" onClick={() => setShowPreview(false)}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="preview-content">
                {formData.channel === 'email' ? (
                  <div dangerouslySetInnerHTML={{ __html: previewText }} />
                ) : (
                  <p>{previewText}</p>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn-primary"
                onClick={() => setShowPreview(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationTemplates;
